import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ENVIRONMENTS,
  Environment,
  RecordResultInput,
  SEVERITIES,
  Severity,
  UatResult,
  recordUatResult,
} from "../../../../utils/icGovernance";
import ActionError from "../detail/ActionError";
import { DetailOf, ItemActionProps } from "../detail/types";
import { TEXT, primaryButtonSx, todayIso } from "../detail/ui";
import { useGovAction } from "../detail/useGovAction";
import { UAT_RESULT_LABEL, itemUrlById, severityLabel } from "../governanceStatus";

const RESULTS: UatResult[] = ["PASS", "FAIL", "BLOCKED"];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * "Record result" form of a UAT test (shown when `permissions.recordResult`).
 * Fail reveals a ticked "Create a linked issue" with title, severity and
 * environment.
 */
const RecordResultForm = ({ item, onUpdated, onReload }: { item: DetailOf<"UAT_TEST"> } & ItemActionProps) => {
  const [result, setResult] = useState<UatResult | "">("");
  const [testDate, setTestDate] = useState(todayIso);
  const [actual, setActual] = useState("");
  const [createIssue, setCreateIssue] = useState(true);
  const [issueTitle, setIssueTitle] = useState("");
  const [severity, setSeverity] = useState<Severity>("S2");
  const [environment, setEnvironment] = useState<Environment>("UAT");
  const action = useGovAction();

  const heading =
    item.status === "NOT_RUN" ? "Record result" : item.retestRequired ? "Record retest result" : "Record a new result";
  const dateOk = !testDate || DATE_RE.test(testDate);

  const reset = () => {
    setResult("");
    setTestDate(todayIso());
    setActual("");
    setCreateIssue(true);
    setIssueTitle("");
    setSeverity("S2");
    setEnvironment("UAT");
  };

  const submit = async () => {
    if (!result || !dateOk) return;
    const body: RecordResultInput = { result };
    if (actual.trim()) body.actualResult = actual.trim();
    if (testDate) body.testDate = testDate;
    if (result === "FAIL" && createIssue) {
      body.createIssue = { severity, environment, ...(issueTitle.trim() ? { title: issueTitle.trim() } : {}) };
    }
    const detail = await action.run(() => recordUatResult(item.id, body));
    if (!detail) return;
    const created = detail.createdIssue;
    reset();
    onUpdated(detail, {
      text: `Result recorded: ${UAT_RESULT_LABEL[result]}`,
      link: created
        ? {
            before: "Created linked issue",
            label: created.displayId || "the new issue",
            to: itemUrlById(created.displayId || created.id),
          }
        : undefined,
    });
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      sx={{ mt: 2, pt: 2, borderTop: "1px solid #f0f0f3" }}
    >
      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, color: TEXT }}>
        {heading}
      </Typography>

      <FormControl component="fieldset" sx={{ mt: 1 }} disabled={action.busy}>
        <FormLabel component="legend" sx={{ fontSize: 13 }}>
          Result
        </FormLabel>
        <RadioGroup row value={result} onChange={(e) => setResult(e.target.value as UatResult)}>
          {RESULTS.map((r) => (
            <FormControlLabel
              key={r}
              value={r}
              control={<Radio />}
              label={UAT_RESULT_LABEL[r]}
              sx={{ minHeight: 44, mr: 3 }}
            />
          ))}
        </RadioGroup>
      </FormControl>

      <TextField
        type="date"
        label="Test date"
        value={testDate}
        onChange={(e) => setTestDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        size="small"
        error={!dateOk}
        helperText={dateOk ? undefined : "Use YYYY-MM-DD"}
        disabled={action.busy}
        sx={{ mt: 1.5, minWidth: 200, display: "flex", maxWidth: 260 }}
      />

      <TextField
        label="Actual result"
        value={actual}
        onChange={(e) => setActual(e.target.value)}
        multiline
        minRows={3}
        fullWidth
        disabled={action.busy}
        sx={{ mt: 2 }}
      />

      {result === "FAIL" ? (
        <Box sx={{ mt: 2, p: 1.5, border: "1px solid #f3d4d4", backgroundColor: "#fffafa", borderRadius: "10px" }}>
          <FormControlLabel
            control={<Checkbox checked={createIssue} onChange={(e) => setCreateIssue(e.target.checked)} />}
            label="Create a linked issue"
            disabled={action.busy}
            sx={{ minHeight: 44 }}
          />
          {createIssue ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Issue title"
                placeholder={`UAT failure: ${item.requirement || item.title}`}
                value={issueTitle}
                onChange={(e) => setIssueTitle(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
                disabled={action.busy}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  label="Severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as Severity)}
                  size="small"
                  fullWidth
                  disabled={action.busy}
                >
                  {SEVERITIES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {severityLabel(s)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Environment"
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value as Environment)}
                  size="small"
                  fullWidth
                  disabled={action.busy}
                >
                  {ENVIRONMENTS.map((env) => (
                    <MenuItem key={env} value={env}>
                      {env}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          ) : null}
        </Box>
      ) : null}

      <ActionError action={action} onReload={onReload} />
      <Box sx={{ textAlign: "right", mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={!result || !dateOk || action.busy}
          startIcon={action.busy ? <CircularProgress size={16} color="inherit" /> : null}
          sx={primaryButtonSx}
        >
          {action.busy ? "Saving…" : "Save result"}
        </Button>
      </Box>
    </Box>
  );
};

export default RecordResultForm;
